<template>
  <div class="main container-fluid dashboard-page">
    <header class="dashboard-hero">
      <div>
        <h4 class="dashboard-hero__title mb-1">Дашборд</h4>
        <p class="dashboard-hero__subtitle mb-0">
          {{ activeTab === 'overview' ? 'Статистика по AI, поиску изображений и контенту' : 'Посещаемость WordPress-сайтов и синхронизация данных' }}
        </p>
      </div>
      <button class="btn btn-outline-light btn-sm dashboard-hero__refresh" :disabled="isActiveTabBusy" @click="refreshActiveTab">
        <i class="bi bi-arrow-repeat me-1"></i>
        Обновить
      </button>
    </header>

    <section class="px-3 pb-2">
      <div class="dashboard-tabs" role="tablist" aria-label="Разделы дашборда">
        <button
          type="button"
          class="dashboard-tab"
          :class="{ 'dashboard-tab--active': activeTab === 'overview' }"
          :disabled="isActiveTabBusy"
          @click="activeTab = 'overview'"
        >
          Обзор
        </button>
        <button
          type="button"
          class="dashboard-tab"
          :class="{ 'dashboard-tab--active': activeTab === 'wordpress' }"
          :disabled="isActiveTabBusy"
          @click="activeTab = 'wordpress'"
        >
          Посещаемость
        </button>
      </div>
    </section>

    <template v-if="activeTab === 'overview'">
      <section class="px-3 pb-2">
        <div class="dashboard-filter-card dashboard-filter-card--wp">
          <div class="dashboard-filter-card__title">Фильтры</div>
          <div class="dashboard-wp-stats-toolbar">
            <div class="row g-2 align-items-center dashboard-wp-stats-toolbar__top">
              <div class="col-12 col-xl-5">
                <div class="dashboard-select-wrap" :class="{ 'dashboard-select-wrap--disabled': loading }">
                  <select v-model="selectedStreamId" class="form-select form-select-sm" :disabled="loading">
                    <option value="">Все стримы</option>
                    <option v-for="stream in streams" :key="stream.id" :value="String(stream.id)">
                      {{ stream.name || `Stream #${stream.id}` }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-12 col-xl-7">
                <div class="dashboard-range-preset dashboard-range-preset--stats">
                  <button
                    v-for="option in overviewRangePresetOptions"
                    :key="`overview-range-${option.key}`"
                    type="button"
                    class="dashboard-chart-toggle"
                    :class="{ 'dashboard-chart-toggle--active': overviewRangePreset === option.key }"
                    :disabled="loading"
                    @click="applyOverviewRangePreset(option.key, { withFetch: true })"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>
            </div>

            <div v-if="overviewRangePreset === 'custom'" class="dashboard-wp-custom-range">
              <div class="row g-2 align-items-end">
                <div class="col-12 col-md-6 col-xl-4">
                  <div class="input-group input-group-sm">
                    <span class="input-group-text">От</span>
                    <input v-model="fromDate" type="date" class="form-control" :disabled="loading" />
                  </div>
                </div>
                <div class="col-12 col-md-6 col-xl-4">
                  <div class="input-group input-group-sm">
                    <span class="input-group-text">До</span>
                    <input v-model="toDate" type="date" class="form-control" :disabled="loading" />
                  </div>
                </div>
                <div class="col-12 col-xl-4">
                  <div class="dashboard-filter-actions dashboard-filter-actions--stats">
                    <button class="btn btn-primary btn-sm" :disabled="loading" @click="applyOverviewFilters">Показать</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div v-if="error" class="alert alert-danger mx-3 my-2">{{ error }}</div>

      <div v-if="loading" class="loading-wrap px-3 pb-2">
        <div class="skeleton-row" v-for="i in 6" :key="i"></div>
      </div>

      <template v-else>
        <div class="dashboard-chips px-3 py-2">
          <span class="dashboard-chip">Период: {{ activeRangeLabel }}</span>
          <span class="dashboard-chip" v-if="activeStreamLabel">Стрим: {{ activeStreamLabel }}</span>
        </div>

        <div class="row g-2 px-3 pb-2">
          <div class="col-12 col-md-6 col-xl-4" v-for="item in statsCards" :key="item.key">
            <article class="dashboard-stat" :class="item.modifier">
              <div class="dashboard-stat__head">
                <div class="dashboard-stat__label">{{ item.label }}</div>
                <div class="dashboard-stat__icon"><i :class="item.icon"></i></div>
              </div>
              <div class="dashboard-stat__value-wrap">
                <div class="dashboard-stat__value">{{ item.value }}</div>
                <span
                  v-if="formatCardChange(item.changeValue)"
                  class="dashboard-stat__change"
                  :class="getCardChangeClass(item.changeValue)"
                >
                  {{ formatCardChange(item.changeValue) }}
                </span>
              </div>
              <div v-if="item.meta" class="dashboard-stat__meta">{{ item.meta }}</div>
            </article>
          </div>
        </div>

        <div class="px-3 pb-3">
          <section class="dashboard-panel mb-3">
            <div class="dashboard-panel__header">Стоимость по стримам ($)</div>
            <div class="table-responsive">
              <table class="table table-sm align-middle mb-0 dashboard-table">
                <thead>
                  <tr>
                    <th>Стрим</th>
                    <th class="text-end">LLM</th>
                    <th class="text-end">Поиск картинок</th>
                    <th class="text-end">cmaker</th>
                    <th class="text-end">Всего</th>
                  </tr>
                </thead>
                <tbody v-if="spendByStreams.length">
                  <tr v-for="row in spendByStreams" :key="row.streamId">
                    <td>{{ row.streamName || `Stream #${row.streamId}` }}</td>
                    <td class="text-end">{{ usd(row.directLlmUsd) }}</td>
                    <td class="text-end">{{ usd(row.imageSearchUsd) }}</td>
                    <td class="text-end">{{ usd(row.cmakerUsd) }}</td>
                    <td class="text-end fw-semibold">{{ usd(row.totalUsd) }}</td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr><td colspan="5" class="text-center text-muted py-3">Нет данных</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="dashboard-panel mb-3">
            <div class="dashboard-panel__header">Стоимость по моделям ($)</div>
            <div class="table-responsive">
              <table class="table table-sm align-middle mb-0 dashboard-table">
                <thead>
                  <tr>
                    <th>Провайдер</th>
                    <th>Модель</th>
                    <th class="text-end">Стоимость</th>
                  </tr>
                </thead>
                <tbody v-if="spendByModels.length">
                  <tr v-for="(row, idx) in spendByModels" :key="`${row.provider || 'N/A'}-${row.model || 'N/A'}-${idx}`">
                    <td>{{ row.provider || '—' }}</td>
                    <td>{{ row.model || '—' }}</td>
                    <td class="text-end fw-semibold">{{ usd(row.costUsd) }}</td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr><td colspan="3" class="text-center text-muted py-3">Нет данных</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="dashboard-panel">
            <div class="dashboard-panel__header">Публикации по стримам</div>
            <div class="table-responsive">
              <table class="table table-sm align-middle mb-0 dashboard-table">
                <thead>
                  <tr><th>Стрим</th><th class="text-end">Публикаций</th></tr>
                </thead>
                <tbody v-if="publishedByStreams.length">
                  <tr v-for="row in publishedByStreams" :key="row.streamId">
                    <td>{{ row.streamName || `Stream #${row.streamId}` }}</td>
                    <td class="text-end fw-semibold">{{ n(row.publishedCount) }}</td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr><td colspan="2" class="text-center text-muted py-3">Нет данных</td></tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </template>
    </template>

    <template v-else>
      <section class="px-3 pb-2">
        <div class="dashboard-filter-card dashboard-filter-card--wp">
          <div class="dashboard-filter-card__title">Синхронизация данных</div>
          <div class="row g-2 align-items-end">
            <div class="col-12 col-xl-5">
              <div class="dashboard-select-wrap" :class="{ 'dashboard-select-wrap--disabled': isWpBusy }">
                <select v-model="wpSyncStreamId" class="form-select form-select-sm" :disabled="isWpBusy">
                  <option value="">Выберите стрим</option>
                  <option v-for="stream in wordpressStreams" :key="`wp-sync-stream-${stream.id}`" :value="String(stream.id)">
                    {{ stream.name || `Stream #${stream.id}` }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col-12 col-xl-7">
              <div class="dashboard-filter-actions">
                <button class="btn btn-outline-secondary btn-sm" :disabled="isWpBusy" @click="syncWordpressAll">
                  <span v-if="wpSyncingAll" class="spinner-border spinner-border-sm me-1"></span>
                  Синхронизировать все
                </button>
                <button
                  class="btn btn-outline-primary btn-sm"
                  :disabled="isWpBusy || !wpSyncStreamId"
                  :title="wpSyncStreamId ? '' : 'Выберите WordPress-стрим'"
                  @click="syncWordpressByStream"
                >
                  <span v-if="wpSyncingStream" class="spinner-border spinner-border-sm me-1"></span>
                  Синхронизировать выбранный
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="px-3 pb-2">
        <div class="dashboard-filter-card dashboard-filter-card--wp">
          <div class="dashboard-filter-card__title">Статистика WordPress</div>
          <div class="dashboard-wp-stats-toolbar">
            <div class="row g-2 align-items-center dashboard-wp-stats-toolbar__top">
              <div class="col-12 col-xl-5">
                <div class="dashboard-select-wrap" :class="{ 'dashboard-select-wrap--disabled': isWpBusy }">
                  <select v-model="wpSelectedStreamId" class="form-select form-select-sm" :disabled="isWpBusy">
                    <option value="">Все WordPress-стримы</option>
                    <option v-for="stream in wordpressStreams" :key="`wp-stream-${stream.id}`" :value="String(stream.id)">
                      {{ stream.name || `Stream #${stream.id}` }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-12 col-xl-7">
                <div class="dashboard-range-preset dashboard-range-preset--stats">
                  <button
                    v-for="option in wpRangePresetOptions"
                    :key="`wp-range-${option.key}`"
                    type="button"
                    class="dashboard-chart-toggle"
                    :class="{ 'dashboard-chart-toggle--active': wpRangePreset === option.key }"
                    :disabled="isWpBusy"
                    @click="applyWpRangePreset(option.key, { withFetch: true })"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>
            </div>

            <div v-if="wpRangePreset === 'custom'" class="dashboard-wp-custom-range">
              <div class="row g-2 align-items-end">
                <div class="col-12 col-md-6 col-xl-4">
                  <div class="input-group input-group-sm">
                    <span class="input-group-text">От</span>
                    <input v-model="wpFromDate" type="date" class="form-control" :disabled="isWpBusy" />
                  </div>
                </div>
                <div class="col-12 col-md-6 col-xl-4">
                  <div class="input-group input-group-sm">
                    <span class="input-group-text">До</span>
                    <input v-model="wpToDate" type="date" class="form-control" :disabled="isWpBusy" />
                  </div>
                </div>
                <div class="col-12 col-xl-4">
                  <div class="dashboard-filter-actions dashboard-filter-actions--stats">
                    <button class="btn btn-primary btn-sm" :disabled="isWpBusy" @click="applyWpFilters">Показать</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div v-if="wpError" class="alert alert-danger mx-3 my-2">{{ wpError }}</div>
      <div v-if="wpSyncMessage" class="alert alert-success mx-3 my-2">{{ wpSyncMessage }}</div>

      <div v-if="wpLoading" class="loading-wrap px-3 pb-2">
        <div class="skeleton-row" v-for="i in 6" :key="`wp-skeleton-${i}`"></div>
      </div>

      <template v-else>
        <div class="dashboard-chips px-3 py-2">
          <span class="dashboard-chip">Период: {{ wpActiveRangeLabel }}</span>
          <span class="dashboard-chip">WordPress-стримов: {{ n(wordpressStreams.length) }}</span>
          <span class="dashboard-chip" v-if="wpSelectedStreamLabel">Выбран: {{ wpSelectedStreamLabel }}</span>
          <span class="dashboard-chip" v-if="wpLastUpdatedAt">Обновлено: {{ formatDateTime(wpLastUpdatedAt) }}</span>
        </div>

        <div v-if="!wordpressStreams.length" class="px-3 pb-3">
          <div class="dashboard-empty-state">
            <div class="dashboard-empty-state__title">Нет WordPress-стримов</div>
            <div class="dashboard-empty-state__text">Создайте стрим типа WORDPRESS, чтобы видеть посещаемость и запускать синхронизацию.</div>
          </div>
        </div>

        <template v-else>
          <div class="row g-2 row-cols-2 row-cols-md-3 row-cols-xl-6 px-3 pb-2">
            <div class="col" v-for="item in wpSummaryCards" :key="item.key">
              <article class="dashboard-stat" :class="[item.modifier, 'dashboard-stat--compact']">
                <div class="dashboard-stat__head">
                  <div class="dashboard-stat__label">{{ item.label }}</div>
                  <div class="dashboard-stat__icon"><i :class="item.icon"></i></div>
                </div>
                <div class="dashboard-stat__value-wrap">
                  <div class="dashboard-stat__value">{{ item.value }}</div>
                  <span
                    v-if="formatCardChange(item.changeValue)"
                    class="dashboard-stat__change"
                    :class="getCardChangeClass(item.changeValue)"
                  >
                    {{ formatCardChange(item.changeValue) }}
                  </span>
                </div>
                <div v-if="item.meta" class="dashboard-stat__meta">{{ item.meta }}</div>
              </article>
            </div>
          </div>

          <div class="px-3 pb-3">
            <section class="dashboard-panel mb-3" v-if="!wpSelectedStreamId">
              <div class="dashboard-panel__header dashboard-panel__header--split">
                <span>Динамика посещаемости (все WordPress-сайты)</span>
                <div class="dashboard-chart-controls">
                  <button
                    v-for="option in wpMetricOptions"
                    :key="`metric-${option.key}`"
                    type="button"
                    class="dashboard-chart-toggle"
                    :class="{ 'dashboard-chart-toggle--active': wpChartMetric === option.key }"
                    @click="wpChartMetric = option.key"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>
              <div class="p-3" v-if="wpTotalChartModel.circles.length">
                <div class="dashboard-chart-meta">
                  <span class="dashboard-chip">Метрика: {{ wpSelectedMetricLabel }}</span>
                  <span class="dashboard-chip">Пик: {{ n(wpTotalChartModel.yMax) }}</span>
                </div>
                <div class="dashboard-line-chart">
                  <svg :viewBox="wpTotalChartModel.viewBox" preserveAspectRatio="none" role="img" aria-label="График посещаемости WordPress">
                    <defs>
                      <linearGradient id="wp-total-area-gradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stop-color="#2f7ed4" stop-opacity="0.35" />
                        <stop offset="100%" stop-color="#2f7ed4" stop-opacity="0.04" />
                      </linearGradient>
                    </defs>
                    <g v-for="tick in wpTotalChartModel.yTicks" :key="`wp-total-tick-${tick.label}-${tick.y}`">
                      <line
                        :x1="wpTotalChartModel.plotLeft"
                        :y1="tick.y"
                        :x2="wpTotalChartModel.plotRight"
                        :y2="tick.y"
                        stroke="#dbe6f8"
                        stroke-width="1"
                      />
                      <text
                        :x="wpTotalChartModel.plotRight + 8"
                        :y="tick.y + 4"
                        class="dashboard-line-chart__tick-label"
                      >
                        {{ tick.label }}
                      </text>
                    </g>
                    <path :d="wpTotalChartModel.areaPath" fill="url(#wp-total-area-gradient)" />
                    <path :d="wpTotalChartModel.linePath" fill="none" stroke="#2f7ed4" stroke-width="3" stroke-linecap="round" />
                    <g v-for="point in wpTotalChartModel.circles" :key="`wp-total-circle-${point.date}`">
                      <circle :cx="point.x" :cy="point.y" r="3.5" fill="#1f5ea6">
                        <title>{{ point.date }} · {{ n(point.value) }}</title>
                      </circle>
                    </g>
                  </svg>
                </div>
                <div class="dashboard-line-chart__axis">
                  <span>{{ wpTotalChartModel.xStart || '—' }}</span>
                  <span>{{ wpTotalChartModel.xEnd || '—' }}</span>
                </div>
              </div>
              <div v-else class="p-3 text-muted">Нет данных в выбранном периоде.</div>
            </section>

            <section class="dashboard-panel mb-3" v-if="wpSelectedStreamId">
              <div class="dashboard-panel__header dashboard-panel__header--split">
                <span>Динамика выбранного стрима: {{ wpSelectedStreamLabel }}</span>
                <div class="dashboard-chart-controls">
                  <button
                    v-for="option in wpMetricOptions"
                    :key="`selected-metric-${option.key}`"
                    type="button"
                    class="dashboard-chart-toggle"
                    :class="{ 'dashboard-chart-toggle--active': wpChartMetric === option.key }"
                    @click="wpChartMetric = option.key"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>
              <div class="p-3" v-if="wpSelectedChartLoading">
                <div class="skeleton-row mb-0"></div>
              </div>
              <div class="p-3" v-else-if="wpSelectedStreamChartModel.circles.length">
                <div class="dashboard-chart-meta">
                  <span class="dashboard-chip">Метрика: {{ wpSelectedMetricLabel }}</span>
                  <span class="dashboard-chip">Пик: {{ n(wpSelectedStreamChartModel.yMax) }}</span>
                  <span class="dashboard-chip">Визиты: {{ n(wpSelectedStreamSummary.visits) }}</span>
                  <span class="dashboard-chip">Уникальные: {{ n(wpSelectedStreamSummary.uniqueVisitors) }}</span>
                  <span class="dashboard-chip">Actions: {{ n(wpSelectedStreamSummary.actions) }}</span>
                </div>
                <div class="dashboard-line-chart">
                  <svg :viewBox="wpSelectedStreamChartModel.viewBox" preserveAspectRatio="none" role="img" aria-label="График выбранного WordPress-стрима">
                    <defs>
                      <linearGradient id="wp-stream-area-gradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stop-color="#2f7ed4" stop-opacity="0.35" />
                        <stop offset="100%" stop-color="#2f7ed4" stop-opacity="0.04" />
                      </linearGradient>
                    </defs>
                    <g v-for="tick in wpSelectedStreamChartModel.yTicks" :key="`wp-stream-tick-${tick.label}-${tick.y}`">
                      <line
                        :x1="wpSelectedStreamChartModel.plotLeft"
                        :y1="tick.y"
                        :x2="wpSelectedStreamChartModel.plotRight"
                        :y2="tick.y"
                        stroke="#dbe6f8"
                        stroke-width="1"
                      />
                      <text
                        :x="wpSelectedStreamChartModel.plotRight + 8"
                        :y="tick.y + 4"
                        class="dashboard-line-chart__tick-label"
                      >
                        {{ tick.label }}
                      </text>
                    </g>
                    <path :d="wpSelectedStreamChartModel.areaPath" fill="url(#wp-stream-area-gradient)" />
                    <path :d="wpSelectedStreamChartModel.linePath" fill="none" stroke="#2f7ed4" stroke-width="3" stroke-linecap="round" />
                    <g v-for="point in wpSelectedStreamChartModel.circles" :key="`wp-stream-circle-${point.date}`">
                      <circle :cx="point.x" :cy="point.y" r="3.5" fill="#1f5ea6">
                        <title>{{ point.date }} · {{ n(point.value) }}</title>
                      </circle>
                    </g>
                  </svg>
                </div>
                <div class="dashboard-line-chart__axis">
                  <span>{{ wpSelectedStreamChartModel.xStart || '—' }}</span>
                  <span>{{ wpSelectedStreamChartModel.xEnd || '—' }}</span>
                </div>
              </div>
              <div v-else class="p-3 text-muted">Нет данных по выбранному стриму за этот период.</div>
            </section>

            <section class="dashboard-panel mb-3" v-if="!wpSelectedStreamId">
              <div class="dashboard-panel__header">Сводка по WordPress-стримам</div>
              <div class="table-responsive">
                <table class="table table-sm align-middle mb-0 dashboard-table">
                  <thead>
                    <tr>
                      <th>Стрим</th>
                      <th class="text-end">Дней</th>
                      <th class="text-end">Визиты</th>
                      <th class="text-end">Уникальные</th>
                      <th class="text-end">Actions</th>
                      <th class="text-end">Avg Bounce %</th>
                      <th class="text-end">Последняя дата</th>
                    </tr>
                  </thead>
                  <tbody v-if="wpStreamRows.length">
                    <tr v-for="row in wpStreamRows" :key="`wp-stream-row-${row.streamId}`">
                      <td>{{ row.streamName || `Stream #${row.streamId}` }}</td>
                      <td class="text-end">{{ n(row.daysCount) }}</td>
                      <td class="text-end fw-semibold">{{ n(row.totalVisits) }}</td>
                      <td class="text-end">{{ n(row.totalUniqueVisitors) }}</td>
                      <td class="text-end">{{ n(row.totalActions) }}</td>
                      <td class="text-end">{{ formatPercent(row.avgBounceRatePercent) }}</td>
                      <td class="text-end">{{ row.lastDate || '—' }}</td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr><td colspan="7" class="text-center text-muted py-3">Нет данных по стримам.</td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section class="dashboard-panel">
              <div class="dashboard-panel__header">Результаты синхронизации</div>
              <div class="p-3" v-if="!lastStreamSync && !lastBulkSync">
                <div class="dashboard-empty-state dashboard-empty-state--compact">
                  <div class="dashboard-empty-state__text">Синхронизация еще не запускалась в текущей сессии.</div>
                </div>
              </div>

              <div v-if="lastStreamSync" class="px-3 pt-3">
                <div class="dashboard-sync-title">Последняя синхронизация выбранного стрима</div>
                <div class="dashboard-sync-grid">
                  <div class="dashboard-sync-item">
                    <span class="dashboard-sync-item__label">Стрим</span>
                    <span class="dashboard-sync-item__value">{{ n(lastStreamSync.streamId) }}</span>
                  </div>
                  <div class="dashboard-sync-item">
                    <span class="dashboard-sync-item__label">Период</span>
                    <span class="dashboard-sync-item__value">{{ lastStreamSync.fromDate || '—' }} - {{ lastStreamSync.toDate || '—' }}</span>
                  </div>
                  <div class="dashboard-sync-item">
                    <span class="dashboard-sync-item__label">Дней запрошено</span>
                    <span class="dashboard-sync-item__value">{{ n(lastStreamSync.requestedDays) }}</span>
                  </div>
                  <div class="dashboard-sync-item">
                    <span class="dashboard-sync-item__label">Дней синхронизировано</span>
                    <span class="dashboard-sync-item__value">{{ n(lastStreamSync.syncedDays) }}</span>
                  </div>
                  <div class="dashboard-sync-item">
                    <span class="dashboard-sync-item__label">Ошибочных дней</span>
                    <span class="dashboard-sync-item__value">{{ n(lastStreamSync.failedDays) }}</span>
                  </div>
                </div>
                <div v-if="lastStreamSync.errors.length" class="alert alert-warning py-2 px-3 mt-2 mb-0">
                  <div class="fw-semibold mb-1">Ошибки синхронизации:</div>
                  <ul class="mb-0">
                    <li v-for="(syncError, idx) in lastStreamSync.errors" :key="`stream-sync-error-${idx}`">{{ syncError }}</li>
                  </ul>
                </div>
              </div>

              <div v-if="lastBulkSync" class="p-3">
                <div class="dashboard-sync-title">Последняя массовая синхронизация</div>
                <div class="dashboard-chips py-2 px-0">
                  <span class="dashboard-chip">Период: {{ lastBulkSync.fromDate || '—' }} - {{ lastBulkSync.toDate || '—' }}</span>
                  <span class="dashboard-chip">Всего WP: {{ n(lastBulkSync.totalWordPressStreams) }}</span>
                  <span class="dashboard-chip">Готово к sync: {{ n(lastBulkSync.eligibleStreams) }}</span>
                  <span class="dashboard-chip">SYNCED: {{ n(lastBulkSync.syncedStreams) }}</span>
                  <span class="dashboard-chip">FAILED: {{ n(lastBulkSync.failedStreams) }}</span>
                  <span class="dashboard-chip">SKIPPED: {{ n(lastBulkSync.skippedStreams) }}</span>
                </div>

                <div class="table-responsive">
                  <table class="table table-sm align-middle mb-0 dashboard-table">
                    <thead>
                      <tr>
                        <th>Стрим</th>
                        <th class="text-center">Статус</th>
                        <th class="text-end">Requested</th>
                        <th class="text-end">Synced</th>
                        <th class="text-end">Failed</th>
                        <th>Сообщение</th>
                      </tr>
                    </thead>
                    <tbody v-if="lastBulkSync.items.length">
                      <tr v-for="item in lastBulkSync.items" :key="`bulk-sync-item-${item.streamId}-${item.status}`">
                        <td>{{ item.streamName || `Stream #${item.streamId}` }}</td>
                        <td class="text-center"><span :class="syncStatusClass(item.status)">{{ item.status || '—' }}</span></td>
                        <td class="text-end">{{ item.requestedDays == null ? '—' : n(item.requestedDays) }}</td>
                        <td class="text-end">{{ item.syncedDays == null ? '—' : n(item.syncedDays) }}</td>
                        <td class="text-end">{{ item.failedDays == null ? '—' : n(item.failedDays) }}</td>
                        <td>{{ resolveBulkSyncMessage(item) }}</td>
                      </tr>
                    </tbody>
                    <tbody v-else>
                      <tr><td colspan="6" class="text-center text-muted py-3">Детализация отсутствует.</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          </div>
        </template>
      </template>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import { computed, onMounted, ref, watch } from 'vue'
import {
  getWordpressVisitsStreamStats,
  getWordpressVisitsStreamsChart,
  getWordpressVisitsTotalChart,
  syncAllWordpressVisits,
  syncWordpressVisitsByStream,
} from '@/services/wordpressVisitsService'

const numberFormatter = new Intl.NumberFormat('ru-RU')
const usdFormatter = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'USD' })
const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000
const MAX_DATE_RANGE_DAYS = 366

function formatDateOffset(days) {
  const d = new Date()
  d.setDate(d.getDate() + days)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function validateDateRange(fromDate, toDate) {
  if (fromDate && toDate && fromDate > toDate) {
    return 'Дата "От" не может быть больше даты "До".'
  }

  if (fromDate && toDate) {
    const from = new Date(`${fromDate}T00:00:00`)
    const to = new Date(`${toDate}T00:00:00`)
    if (!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime())) {
      const rangeDays = Math.floor((to.getTime() - from.getTime()) / ONE_DAY_IN_MS) + 1
      if (rangeDays > MAX_DATE_RANGE_DAYS) {
        return `Максимальный диапазон дат: ${MAX_DATE_RANGE_DAYS} дней.`
      }
    }
  }

  return ''
}

function toDateTimestamp(value) {
  const ms = new Date(value).getTime()
  return Number.isFinite(ms) ? ms : 0
}

function normalizeHttpErrorMessage(error, fallbackMessage, options = {}) {
  const status = Number(error?.response?.status || 0)
  if (status === 403) return options.forbiddenMessage || 'Недостаточно прав для выполнения действия.'
  if (status === 404) return options.notFoundMessage || 'Ресурс не найден.'
  if (status === 400) return options.badRequestMessage || 'Проверьте параметры запроса.'
  return fallbackMessage
}

function createLineChartModel(points, valueSelector, options = {}) {
  const width = Number(options.width || 1000)
  const height = Number(options.height || 280)
  const paddingLeft = Number(options.paddingLeft || 24)
  const paddingRight = Number(options.paddingRight || 64)
  const paddingTop = Number(options.paddingTop || 24)
  const paddingBottom = Number(options.paddingBottom || 34)

  const normalizedPoints = Array.isArray(points)
    ? points.map(point => ({
      date: String(point?.date || ''),
      value: Math.max(0, Number(valueSelector(point) || 0)),
    }))
    : []

  if (!normalizedPoints.length) {
    return {
      viewBox: `0 0 ${width} ${height}`,
      linePath: '',
      areaPath: '',
      circles: [],
      yTicks: [],
      plotLeft: paddingLeft,
      plotRight: width - paddingRight,
      plotBottom: height - paddingBottom,
      yMax: 0,
      xStart: '',
      xEnd: '',
    }
  }

  const chartWidth = Math.max(1, width - paddingLeft - paddingRight)
  const chartHeight = Math.max(1, height - paddingTop - paddingBottom)
  const plotBottom = height - paddingBottom
  const plotRight = width - paddingRight
  const maxValue = normalizedPoints.reduce((max, point) => Math.max(max, point.value), 0)
  const yMax = maxValue > 0 ? maxValue : 1

  const yTicks = [
    {
      y: paddingTop,
      value: yMax,
      label: numberFormatter.format(Math.round(yMax)),
    },
    {
      y: plotBottom,
      value: 0,
      label: '0',
    },
  ]

  const circles = normalizedPoints.map((point, index) => {
    const ratioX = normalizedPoints.length > 1 ? index / (normalizedPoints.length - 1) : 0
    const ratioY = point.value / yMax
    const x = paddingLeft + ratioX * chartWidth
    const y = paddingTop + (1 - ratioY) * chartHeight
    return { ...point, x, y }
  })

  const linePath = circles
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(' ')

  const firstPoint = circles[0]
  const lastPoint = circles[circles.length - 1]
  const areaPath = [
    linePath,
    `L ${lastPoint.x.toFixed(2)} ${plotBottom.toFixed(2)}`,
    `L ${firstPoint.x.toFixed(2)} ${plotBottom.toFixed(2)}`,
    'Z',
  ].join(' ')

  return {
    viewBox: `0 0 ${width} ${height}`,
    linePath,
    areaPath,
    circles,
    yTicks,
    plotLeft: paddingLeft,
    plotRight,
    plotBottom,
    yMax,
    xStart: firstPoint?.date || '',
    xEnd: lastPoint?.date || '',
  }
}

function createEmptyWpTotals() {
  return {
    uniqueVisitors: 0,
    visits: 0,
    usersCount: 0,
    actions: 0,
    maxActions: 0,
    bounceCount: 0,
    visitsConverted: 0,
    sumVisitLength: 0,
    avgTimeOnSite: 0,
    bounceRatePercent: 0,
  }
}

function createEmptyWpChangePercent() {
  return {
    uniqueVisitors: null,
    visits: null,
    usersCount: null,
    actions: null,
    maxActions: null,
    bounceCount: null,
    visitsConverted: null,
    sumVisitLength: null,
    avgTimeOnSite: null,
    bounceRatePercent: null,
  }
}

function createEmptyWpDynamics() {
  return {
    previousFromDate: '',
    previousToDate: '',
    currentTotals: createEmptyWpTotals(),
    previousTotals: createEmptyWpTotals(),
    changePercent: createEmptyWpChangePercent(),
  }
}

function createEmptyWpStreamStats() {
  return {
    streamId: 0,
    streamName: '',
    fromDate: '',
    toDate: '',
    points: [],
    hasDynamics: false,
    dynamics: createEmptyWpDynamics(),
  }
}

export default {
  name: 'DashboardPage',
  setup() {
    const streams = ref([])
    const stats = ref(null)
    const loading = ref(false)
    const error = ref('')
    const activeTab = ref('overview')

    const selectedStreamId = ref('')
    const fromDate = ref(formatDateOffset(-29))
    const toDate = ref(formatDateOffset(0))
    const overviewRangePreset = ref('month')

    const wpSyncStreamId = ref('')
    const wpSelectedStreamId = ref('')
    const wpRangePreset = ref('month')
    const wpFromDate = ref(formatDateOffset(-29))
    const wpToDate = ref(formatDateOffset(0))
    const wpLoading = ref(false)
    const wpSyncingAll = ref(false)
    const wpSyncingStream = ref(false)
    const wpError = ref('')
    const wpSyncMessage = ref('')
    const wpTotalChart = ref({ fromDate: '', toDate: '', points: [], hasDynamics: false, dynamics: createEmptyWpDynamics() })
    const wpStreamsChart = ref({ fromDate: '', toDate: '', streams: [] })
    const wpSelectedStreamStats = ref(createEmptyWpStreamStats())
    const wpSelectedChartLoading = ref(false)
    const wpLastUpdatedAt = ref('')
    const lastStreamSync = ref(null)
    const lastBulkSync = ref(null)
    const wpChartMetric = ref('visits')

    const wpMetricOptions = Object.freeze([
      { key: 'visits', label: 'Визиты' },
      { key: 'uniqueVisitors', label: 'Уникальные' },
      { key: 'actions', label: 'Actions' },
    ])

    const rangePresetOptions = Object.freeze([
      { key: 'month', label: 'Месяц' },
      { key: 'week', label: 'Неделя' },
      { key: 'day', label: 'День' },
      { key: 'custom', label: 'Произвольно' },
    ])
    const overviewRangePresetOptions = rangePresetOptions
    const wpRangePresetOptions = rangePresetOptions

    const aiTotals = computed(() => {
      const totals = stats.value?.ai?.totals || {}
      return {
        promptTokens: Number(totals.promptTokens || 0),
        completionTokens: Number(totals.completionTokens || 0),
        totalTokens: Number(totals.totalTokens || 0),
        requestsCount: Number(totals.requestsCount || 0),
      }
    })

    const publishedCount = computed(() => Number(stats.value?.publishedContent?.publishedCount || 0))
    const publishedByStreams = computed(() => (Array.isArray(stats.value?.publishedContent?.byStreams) ? stats.value.publishedContent.byStreams : []))

    const spend = computed(() => stats.value?.spend || {})
    const spendByStreams = computed(() => (Array.isArray(spend.value.byStream) ? spend.value.byStream : []))
    const spendByModels = computed(() => (Array.isArray(spend.value.byModel) ? spend.value.byModel : []))

    const activeStreamsCount = computed(() => {
      const ids = new Set()
      spendByStreams.value.forEach(x => ids.add(String(x.streamId)))
      publishedByStreams.value.forEach(x => ids.add(String(x.streamId)))
      return ids.size
    })

    const activeRangeLabel = computed(() => {
      const from = stats.value?.fromDate || fromDate.value
      const to = stats.value?.toDate || toDate.value
      return `${from || '—'} - ${to || '—'}`
    })

    const activeStreamLabel = computed(() => {
      const id = stats.value?.streamIdFilter
      if (id == null || id === '') return ''
      const normalized = String(id)
      const stream = streams.value.find(s => String(s.id) === normalized)
      return stream?.name || `Stream #${normalized}`
    })

    function n(value) {
      return numberFormatter.format(Number(value || 0))
    }

    function usd(value) {
      return usdFormatter.format(Number(value || 0))
    }

    function formatDateTime(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleString('ru-RU')
    }

    function formatPercent(value) {
      const numeric = Number(value || 0)
      return `${numeric.toFixed(1)}%`
    }

    function formatCardChange(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return ''
      const numeric = Number(value)
      const sign = numeric > 0 ? '+' : ''
      return `${sign}${numeric.toFixed(1)}%`
    }

    function getCardChangeClass(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return ''
      const numeric = Number(value)
      if (numeric > 0) return 'dashboard-stat__change--up'
      if (numeric < 0) return 'dashboard-stat__change--down'
      return 'dashboard-stat__change--flat'
    }

    function formatDuration(secondsValue) {
      const totalSeconds = Math.max(0, Math.round(Number(secondsValue || 0)))
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      return `${minutes} мин ${String(seconds).padStart(2, '0')} сек`
    }

    function getWpRangePresetDates(presetKey) {
      if (presetKey === 'day') return { fromDate: formatDateOffset(0), toDate: formatDateOffset(0) }
      if (presetKey === 'week') return { fromDate: formatDateOffset(-6), toDate: formatDateOffset(0) }
      return { fromDate: formatDateOffset(-29), toDate: formatDateOffset(0) }
    }

    function detectWpRangePreset(from, to) {
      if (!from || !to || from > to) return 'custom'
      if (to !== formatDateOffset(0)) return 'custom'

      const fromDateValue = new Date(`${from}T00:00:00`)
      const toDateValue = new Date(`${to}T00:00:00`)
      if (Number.isNaN(fromDateValue.getTime()) || Number.isNaN(toDateValue.getTime())) return 'custom'

      const daysCount = Math.floor((toDateValue.getTime() - fromDateValue.getTime()) / ONE_DAY_IN_MS) + 1
      if (daysCount === 1) return 'day'
      if (daysCount === 7) return 'week'
      if (daysCount === 30) return 'month'
      return 'custom'
    }

    const statsCards = computed(() => ([
      {
        key: 'spendTotal',
        label: 'Стоимость всего ($)',
        value: usd(spend.value.totalUsd),
        meta: `LLM: ${usd(spend.value.directLlmUsd)} · Картинки: ${usd(spend.value.imageSearchUsd)} · cmaker: ${usd(spend.value.cmakerUsd)}`,
        icon: 'bi bi-cash-coin',
        modifier: 'dashboard-stat--tokens',
      },
      {
        key: 'spendLlm',
        label: 'Стоимость LLM ($)',
        value: usd(spend.value.directLlmUsd),
        meta: 'Прямые запросы к LLM',
        icon: 'bi bi-cpu',
        modifier: 'dashboard-stat--img-req',
      },
      {
        key: 'spendCmaker',
        label: 'Стоимость cmaker ($)',
        value: usd(spend.value.cmakerUsd),
        meta: 'Генерация контента cmaker',
        icon: 'bi bi-robot',
        modifier: 'dashboard-stat--content-req',
      },
      {
        key: 'aiRequests',
        label: 'AI запросы',
        value: n(aiTotals.value.requestsCount),
        meta: 'Количество обращений к AI',
        icon: 'bi bi-lightning-charge',
        modifier: 'dashboard-stat--ai-req',
      },
      {
        key: 'published',
        label: 'Публикации',
        value: n(publishedCount.value),
        meta: 'Опубликованные материалы',
        icon: 'bi bi-send-check',
        modifier: 'dashboard-stat--published',
      },
      {
        key: 'activeStreams',
        label: 'Активные стримы',
        value: n(activeStreamsCount.value),
        meta: 'Суммарно по всем модулям',
        icon: 'bi bi-activity',
        modifier: 'dashboard-stat--streams',
      },
    ]))

    const wordpressStreams = computed(() =>
      streams.value.filter(stream => String(stream?.type || '').toUpperCase() === 'WORDPRESS')
    )

    const isWpBusy = computed(() => wpLoading.value || wpSyncingAll.value || wpSyncingStream.value)
    const isActiveTabBusy = computed(() => (activeTab.value === 'overview' ? loading.value : isWpBusy.value))

    const wpSelectedStreamLabel = computed(() => {
      if (!wpSelectedStreamId.value) return ''
      const selected = wordpressStreams.value.find(stream => String(stream.id) === String(wpSelectedStreamId.value))
      return selected?.name || `Stream #${wpSelectedStreamId.value}`
    })

    const wpTotalPoints = computed(() => {
      const points = Array.isArray(wpTotalChart.value?.points) ? wpTotalChart.value.points : []
      return points.slice().sort((a, b) => toDateTimestamp(a.date) - toDateTimestamp(b.date))
    })

    const wpSelectedPoints = computed(() => {
      const points = Array.isArray(wpSelectedStreamStats.value?.points) ? wpSelectedStreamStats.value.points : []
      return points.slice().sort((a, b) => toDateTimestamp(a.date) - toDateTimestamp(b.date))
    })

    const selectedMetricSelector = point => Number(point?.[wpChartMetric.value] || 0)

    const wpTotalChartModel = computed(() =>
      createLineChartModel(wpTotalPoints.value, selectedMetricSelector)
    )

    const wpSelectedStreamChartModel = computed(() =>
      createLineChartModel(wpSelectedPoints.value, selectedMetricSelector)
    )

    const wpStreamRows = computed(() => {
      const streamsSeries = Array.isArray(wpStreamsChart.value?.streams) ? wpStreamsChart.value.streams : []

      return streamsSeries
        .map(streamSeries => {
          const points = Array.isArray(streamSeries.points) ? streamSeries.points.slice() : []
          points.sort((a, b) => toDateTimestamp(a.date) - toDateTimestamp(b.date))

          const totals = points.reduce(
            (acc, point) => {
              acc.totalVisits += Number(point.visits || 0)
              acc.totalUniqueVisitors += Number(point.uniqueVisitors || 0)
              acc.totalActions += Number(point.actions || 0)
              acc.bounceRateSum += Number(point.bounceRatePercent || 0)
              return acc
            },
            { totalVisits: 0, totalUniqueVisitors: 0, totalActions: 0, bounceRateSum: 0 }
          )

          const lastPoint = points.length ? points[points.length - 1] : null
          const avgBounceRatePercent = points.length ? totals.bounceRateSum / points.length : 0

          return {
            streamId: Number(streamSeries.streamId || 0),
            streamName: streamSeries.streamName || '',
            daysCount: points.length,
            totalVisits: totals.totalVisits,
            totalUniqueVisitors: totals.totalUniqueVisitors,
            totalActions: totals.totalActions,
            avgBounceRatePercent,
            lastDate: lastPoint?.date || '',
          }
        })
        .sort((a, b) => {
          const visitsDiff = b.totalVisits - a.totalVisits
          if (visitsDiff !== 0) return visitsDiff
          return String(a.streamName || '').localeCompare(String(b.streamName || ''), 'ru')
        })
    })

    const wpSummarySourcePoints = computed(() =>
      wpSelectedStreamId.value ? wpSelectedPoints.value : wpTotalPoints.value
    )

    const wpActiveDynamics = computed(() => {
      if (wpSelectedStreamId.value) return wpSelectedStreamStats.value?.dynamics || createEmptyWpDynamics()
      return wpTotalChart.value?.dynamics || createEmptyWpDynamics()
    })

    const wpHasDynamics = computed(() => {
      if (wpSelectedStreamId.value) return Boolean(wpSelectedStreamStats.value?.hasDynamics)
      return Boolean(wpTotalChart.value?.hasDynamics)
    })

    const wpSummary = computed(() => {
      const currentTotals = wpActiveDynamics.value?.currentTotals
      if (wpHasDynamics.value && currentTotals) {
        return {
          totalVisits: Number(currentTotals.visits || 0),
          totalUniqueVisitors: Number(currentTotals.uniqueVisitors || 0),
          totalActions: Number(currentTotals.actions || 0),
          totalVisitsConverted: Number(currentTotals.visitsConverted || 0),
          totalBounceCount: Number(currentTotals.bounceCount || 0),
          avgTimeOnSite: Number(currentTotals.avgTimeOnSite || 0),
          avgBounceRatePercent: Number(currentTotals.bounceRatePercent || 0),
          daysCount: wpSummarySourcePoints.value.length,
        }
      }

      const summary = wpSummarySourcePoints.value.reduce(
        (acc, point) => {
          acc.totalVisits += Number(point.visits || 0)
          acc.totalUniqueVisitors += Number(point.uniqueVisitors || 0)
          acc.totalActions += Number(point.actions || 0)
          acc.totalVisitsConverted += Number(point.visitsConverted || 0)
          acc.totalBounceCount += Number(point.bounceCount || 0)
          acc.avgTimeOnSiteSum += Number(point.avgTimeOnSite || 0)
          acc.bounceRatePercentSum += Number(point.bounceRatePercent || 0)
          return acc
        },
        {
          totalVisits: 0,
          totalUniqueVisitors: 0,
          totalActions: 0,
          totalVisitsConverted: 0,
          totalBounceCount: 0,
          avgTimeOnSiteSum: 0,
          bounceRatePercentSum: 0,
        }
      )

      const pointsCount = wpSummarySourcePoints.value.length
      return {
        ...summary,
        daysCount: pointsCount,
        avgTimeOnSite: pointsCount ? summary.avgTimeOnSiteSum / pointsCount : 0,
        avgBounceRatePercent: pointsCount ? summary.bounceRatePercentSum / pointsCount : 0,
      }
    })

    const wpSummaryCards = computed(() => ([
      {
        key: 'wpVisits',
        label: 'Визиты (сумма)',
        value: n(wpSummary.value.totalVisits),
        changeValue: wpActiveDynamics.value?.changePercent?.visits,
        meta: '',
        icon: 'bi bi-graph-up-arrow',
        modifier: 'dashboard-stat--wp-visits',
      },
      {
        key: 'wpUnique',
        label: 'Уникальные (сумма)',
        value: n(wpSummary.value.totalUniqueVisitors),
        changeValue: wpActiveDynamics.value?.changePercent?.uniqueVisitors,
        meta: '',
        icon: 'bi bi-people',
        modifier: 'dashboard-stat--wp-unique',
      },
      {
        key: 'wpActions',
        label: 'Actions (сумма)',
        value: n(wpSummary.value.totalActions),
        changeValue: wpActiveDynamics.value?.changePercent?.actions,
        meta: '',
        icon: 'bi bi-cursor-fill',
        modifier: 'dashboard-stat--wp-actions',
      },
      {
        key: 'wpAvgTime',
        label: 'Среднее время',
        value: formatDuration(wpSummary.value.avgTimeOnSite),
        changeValue: null,
        meta: '',
        icon: 'bi bi-clock-history',
        modifier: 'dashboard-stat--wp-time',
      },
      {
        key: 'wpBounceRate',
        label: 'Средний Bounce %',
        value: formatPercent(wpSummary.value.avgBounceRatePercent),
        changeValue: wpActiveDynamics.value?.changePercent?.bounceRatePercent,
        meta: '',
        icon: 'bi bi-arrow-return-left',
        modifier: 'dashboard-stat--wp-bounce',
      },
      {
        key: 'wpStreams',
        label: wpSelectedStreamId.value ? 'Показ по стриму' : 'Стримы в отчете',
        value: n(wpSelectedStreamId.value ? 1 : wpStreamRows.value.length),
        meta: '',
        icon: 'bi bi-diagram-3',
        modifier: 'dashboard-stat--wp-streams',
      },
    ]))

    const wpActiveRangeLabel = computed(() => {
      const from = wpTotalChart.value?.fromDate || wpFromDate.value
      const to = wpTotalChart.value?.toDate || wpToDate.value
      return `${from || '—'} - ${to || '—'}`
    })

    const wpSelectedMetricLabel = computed(() => {
      const selected = wpMetricOptions.find(option => option.key === wpChartMetric.value)
      return selected?.label || 'Метрика'
    })

    const wpSelectedStreamSummary = computed(() => {
      const currentTotals = wpSelectedStreamStats.value?.dynamics?.currentTotals
      if (wpSelectedStreamStats.value?.hasDynamics && currentTotals) {
        return {
          visits: Number(currentTotals.visits || 0),
          uniqueVisitors: Number(currentTotals.uniqueVisitors || 0),
          actions: Number(currentTotals.actions || 0),
        }
      }

      return wpSelectedPoints.value.reduce(
        (acc, point) => {
          acc.visits += Number(point.visits || 0)
          acc.uniqueVisitors += Number(point.uniqueVisitors || 0)
          acc.actions += Number(point.actions || 0)
          return acc
        },
        { visits: 0, uniqueVisitors: 0, actions: 0 }
      )
    })

    const syncStatusClass = status => {
      const normalized = String(status || '').toUpperCase()
      if (normalized === 'SYNCED') return 'badge text-bg-success'
      if (normalized === 'FAILED') return 'badge text-bg-danger'
      if (normalized === 'SKIPPED') return 'badge text-bg-warning text-dark'
      return 'badge text-bg-secondary'
    }

    const resolveBulkSyncMessage = item => {
      const status = String(item?.status || '').toUpperCase()
      if (status === 'FAILED' || status === 'SKIPPED') {
        return 'проверьте URL сайта и Credentials в настройках стрима'
      }
      return item?.message || '—'
    }

    const fetchStreams = async () => {
      try {
        const collected = []
        const size = 100
        let page = 0
        let keepLoading = true

        while (keepLoading) {
          const { data } = await axios.get('/api/streams', {
            params: { page, size, sortBy: 'createdAt', sortDir: 'desc' },
          })

          if (Array.isArray(data)) {
            collected.push(...data)
            break
          }

          const chunk = Array.isArray(data?.content) ? data.content : []
          collected.push(...chunk)

          const totalPages = Number(data?.totalPages || 0)
          const isLast = data?.last === true || totalPages === 0 || page >= totalPages - 1
          keepLoading = !isLast
          page += 1
          if (page > 500) keepLoading = false
        }

        streams.value = collected
      } catch (e) {
        console.error('Не удалось загрузить список стримов для фильтра:', e)
      }
    }

    const fetchStatistics = async () => {
      const rangeError = validateDateRange(fromDate.value, toDate.value)
      if (rangeError) {
        error.value = rangeError
        return
      }

      loading.value = true
      error.value = ''

      const params = {}
      if (selectedStreamId.value) params.streamId = Number(selectedStreamId.value)
      if (fromDate.value) params.fromDate = fromDate.value
      if (toDate.value) params.toDate = toDate.value

      try {
        const { data } = await axios.get('/api/user-statistics', { params })
        stats.value = data || {}
      } catch (e) {
        console.error('Ошибка при загрузке статистики пользователя:', e)
        error.value = normalizeHttpErrorMessage(e, 'Не удалось загрузить статистику. Попробуйте обновить страницу.', {
          forbiddenMessage: 'У вас нет доступа к статистике аккаунта.',
        })
      } finally {
        loading.value = false
      }
    }

    const fetchWordpressCharts = async ({ strictRange = true } = {}) => {
      const rangeError = validateDateRange(wpFromDate.value, wpToDate.value)
      const hasRangeError = Boolean(rangeError)

      if (hasRangeError && strictRange) {
        wpError.value = rangeError
        return
      }

      const chartDateParams = hasRangeError
        ? {}
        : { fromDate: wpFromDate.value, toDate: wpToDate.value }

      wpLoading.value = true
      wpError.value = ''
      wpSyncMessage.value = ''

      try {
        const requests = [
          getWordpressVisitsTotalChart(chartDateParams),
          getWordpressVisitsStreamsChart(chartDateParams),
        ]

        if (wpSelectedStreamId.value) {
          requests.push(getWordpressVisitsStreamStats(Number(wpSelectedStreamId.value), chartDateParams))
          wpSelectedChartLoading.value = true
        } else {
          requests.push(Promise.resolve(createEmptyWpStreamStats()))
        }

        const [totalChartResponse, streamsChartResponse, selectedStreamResponse] = await Promise.all(requests)

        wpTotalChart.value = totalChartResponse
        wpStreamsChart.value = streamsChartResponse
        wpSelectedStreamStats.value = selectedStreamResponse || createEmptyWpStreamStats()
        wpLastUpdatedAt.value = new Date().toISOString()

        if (hasRangeError) {
          wpError.value = 'Невалидный диапазон дат. Показаны данные по умолчанию за последние 30 дней.'
        }
      } catch (e) {
        console.error('Ошибка при загрузке WordPress Visits статистики:', e)
        wpError.value = normalizeHttpErrorMessage(e, 'Не удалось загрузить статистику WordPress. Попробуйте позже.', {
          forbiddenMessage: 'Нет доступа к WordPress Visits статистике.',
          badRequestMessage: 'Некорректный диапазон дат для WordPress Visits статистики.',
        })
      } finally {
        wpLoading.value = false
        wpSelectedChartLoading.value = false
      }
    }

    const syncWordpressAll = async () => {
      wpSyncingAll.value = true
      wpError.value = ''
      wpSyncMessage.value = ''

      try {
        const response = await syncAllWordpressVisits()
        lastBulkSync.value = response
        wpSyncMessage.value = 'Массовая синхронизация завершена.'
        await fetchWordpressCharts({ strictRange: false })
      } catch (e) {
        console.error('Ошибка при массовой синхронизации WordPress Visits:', e)
        wpError.value = normalizeHttpErrorMessage(e, 'Не удалось выполнить массовую синхронизацию.', {
          forbiddenMessage: 'Нет доступа к синхронизации WordPress Visits.',
          badRequestMessage: 'Проверьте настройки WordPress-стримов и credentials.',
        })
      } finally {
        wpSyncingAll.value = false
      }
    }

    const syncWordpressByStream = async () => {
      const streamId = Number(wpSyncStreamId.value || 0)
      if (!streamId) {
        wpError.value = 'Выберите стрим для синхронизации.'
        return
      }

      wpSyncingStream.value = true
      wpError.value = ''
      wpSyncMessage.value = ''

      try {
        const response = await syncWordpressVisitsByStream({ streamId })
        lastStreamSync.value = response
        wpSyncMessage.value = 'Синхронизация выбранного стрима завершена.'
        await fetchWordpressCharts({ strictRange: false })
      } catch (e) {
        console.error('Ошибка при синхронизации WordPress Visits для стрима:', e)
        wpError.value = normalizeHttpErrorMessage(e, 'Не удалось синхронизировать выбранный стрим.', {
          forbiddenMessage: 'Нет доступа к синхронизации выбранного стрима.',
          notFoundMessage: 'Стрим для синхронизации не найден.',
          badRequestMessage: 'Проверьте настройки стрима и credentials.',
        })
      } finally {
        wpSyncingStream.value = false
      }
    }

    const refreshActiveTab = async () => {
      if (activeTab.value === 'overview') {
        await fetchStatistics()
      } else {
        await fetchWordpressCharts()
      }
    }

    const applyOverviewFilters = async () => {
      await fetchStatistics()
    }

    const resetFilters = async () => {
      selectedStreamId.value = ''
      overviewRangePreset.value = 'month'
      fromDate.value = formatDateOffset(-29)
      toDate.value = formatDateOffset(0)
      await fetchStatistics()
    }

    const applyOverviewRangePreset = async (presetKey, { withFetch = false } = {}) => {
      const normalizedPreset = ['month', 'week', 'day', 'custom'].includes(String(presetKey)) ? String(presetKey) : 'month'
      overviewRangePreset.value = normalizedPreset

      if (normalizedPreset !== 'custom') {
        const nextRange = getWpRangePresetDates(normalizedPreset)
        fromDate.value = nextRange.fromDate
        toDate.value = nextRange.toDate
      }

      if (withFetch && normalizedPreset !== 'custom') {
        await fetchStatistics()
      }
    }

    const applyWpRangePreset = async (presetKey, { withFetch = false } = {}) => {
      const normalizedPreset = ['month', 'week', 'day', 'custom'].includes(String(presetKey)) ? String(presetKey) : 'month'
      wpRangePreset.value = normalizedPreset

      if (normalizedPreset !== 'custom') {
        const nextRange = getWpRangePresetDates(normalizedPreset)
        wpFromDate.value = nextRange.fromDate
        wpToDate.value = nextRange.toDate
      }

      if (withFetch && normalizedPreset !== 'custom') {
        await fetchWordpressCharts()
      }
    }

    const applyWpFilters = async () => {
      await fetchWordpressCharts()
    }

    const resetWpFilters = async () => {
      wpSelectedStreamId.value = ''
      await applyWpRangePreset('month', { withFetch: true })
    }

    watch(activeTab, async nextTab => {
      if (nextTab !== 'wordpress') return
      if (wpLoading.value) return
      const hasBaseData = wpTotalPoints.value.length || wpStreamRows.value.length || wpError.value
      const hasSelectedData = !wpSelectedStreamId.value || Number(wpSelectedStreamStats.value?.streamId || 0) > 0 || wpError.value
      if (hasBaseData && hasSelectedData) return
      await fetchWordpressCharts({ strictRange: false })
    })

    watch(wpSelectedStreamId, async nextStreamId => {
      wpSelectedStreamStats.value = createEmptyWpStreamStats()
      if (!nextStreamId) {
        return
      }

      if (activeTab.value !== 'wordpress') return
      if (wpLoading.value || wpSyncingAll.value || wpSyncingStream.value) return
      await fetchWordpressCharts({ strictRange: false })
    })

    watch(selectedStreamId, async () => {
      if (activeTab.value !== 'overview') return
      if (loading.value) return
      await fetchStatistics()
    })

    watch([wpFromDate, wpToDate], ([nextFromDate, nextToDate]) => {
      if (wpRangePreset.value === 'custom') return
      wpRangePreset.value = detectWpRangePreset(nextFromDate, nextToDate)
    })

    watch([fromDate, toDate], ([nextFromDate, nextToDate]) => {
      if (overviewRangePreset.value === 'custom') return
      overviewRangePreset.value = detectWpRangePreset(nextFromDate, nextToDate)
    })

    onMounted(async () => {
      await fetchStreams()
      await fetchStatistics()
    })

    return {
      streams,
      stats,
      loading,
      error,
      activeTab,
      isActiveTabBusy,
      selectedStreamId,
      fromDate,
      toDate,
      overviewRangePreset,
      overviewRangePresetOptions,
      aiTotals,
      publishedCount,
      publishedByStreams,
      spend,
      spendByStreams,
      spendByModels,
      activeStreamsCount,
      activeRangeLabel,
      activeStreamLabel,
      statsCards,
      usd,
      wpSyncStreamId,
      wpSelectedStreamId,
      wpRangePreset,
      wpRangePresetOptions,
      wpFromDate,
      wpToDate,
      wpChartMetric,
      wpMetricOptions,
      wpLoading,
      wpSyncingAll,
      wpSyncingStream,
      wpSelectedChartLoading,
      isWpBusy,
      wpError,
      wpSyncMessage,
      wordpressStreams,
      wpSelectedStreamLabel,
      wpSelectedMetricLabel,
      wpActiveRangeLabel,
      wpLastUpdatedAt,
      wpTotalPoints,
      wpSelectedPoints,
      wpTotalChartModel,
      wpSelectedStreamChartModel,
      wpSelectedStreamSummary,
      wpSummaryCards,
      wpStreamRows,
      lastStreamSync,
      lastBulkSync,
      n,
      formatDateTime,
      formatPercent,
      formatDuration,
      formatCardChange,
      getCardChangeClass,
      syncStatusClass,
      resolveBulkSyncMessage,
      refreshActiveTab,
      applyOverviewFilters,
      applyOverviewRangePreset,
      resetFilters,
      applyWpFilters,
      resetWpFilters,
      applyWpRangePreset,
      syncWordpressAll,
      syncWordpressByStream,
    }
  },
}
</script>

<style scoped>
.dashboard-page {
  min-height: 100%;
  padding-top: 10px;
  background:
    radial-gradient(circle at 2% 3%, rgba(66, 153, 225, 0.2), transparent 36%),
    radial-gradient(circle at 98% 2%, rgba(56, 189, 248, 0.18), transparent 32%),
    #f3f7ff;
}

.dashboard-hero {
  margin: 0 20px 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: linear-gradient(135deg, #10305f 0%, #1b4f93 60%, #1e67b7 100%);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 18px 40px rgba(16, 48, 95, 0.22);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.dashboard-hero__title {
  font-weight: 800;
  letter-spacing: 0.01em;
}

.dashboard-hero__subtitle {
  color: rgba(232, 242, 255, 0.92);
  font-size: 13px;
}

.dashboard-hero__refresh {
  border-color: rgba(255, 255, 255, 0.45);
  color: #fff;
  white-space: nowrap;
}

.dashboard-hero__refresh:hover,
.dashboard-hero__refresh:focus {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.12);
}

.dashboard-tabs {
  border-radius: 14px;
  border: 1px solid #dce7fa;
  background: #fff;
  padding: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.dashboard-tab {
  border: 1px solid transparent;
  background: transparent;
  color: #3d577f;
  border-radius: 10px;
  padding: 6px 11px;
  font-size: 13px;
  font-weight: 700;
  transition: all 0.15s ease;
}

.dashboard-tab:hover {
  border-color: #d6e4fb;
  background: #f6faff;
}

.dashboard-tab--active {
  color: #1e4f91;
  border-color: #cde0ff;
  background: #edf4ff;
}

.dashboard-filter-card {
  border-radius: 14px;
  border: 1px solid #dce7fa;
  background: #fff;
  padding: 12px;
}

.dashboard-filter-card--wp {
  border-color: #d5e7ef;
  background: linear-gradient(180deg, #ffffff 0%, #fbfeff 100%);
}

.dashboard-filter-card__title {
  display: inline-flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 5px 9px;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: #eff5ff;
  color: #3b5690;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.dashboard-filter-card .form-label {
  color: #355074;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.dashboard-filter-card .form-control,
.dashboard-filter-card .form-select {
  min-height: 34px;
  font-size: 13px;
  border-color: #d9e4f7;
}

.dashboard-filter-card .btn {
  font-size: 13px;
  font-weight: 700;
}

.dashboard-filter-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.dashboard-range-preset {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.dashboard-wp-stats-toolbar__top {
  margin-bottom: 8px;
}

.dashboard-filter-actions--stats {
  justify-content: flex-end;
}

.dashboard-filter-card--wp .dashboard-select-wrap {
  position: relative;
}

.dashboard-filter-card--wp .dashboard-select-wrap::before {
  content: '';
  position: absolute;
  top: 1px;
  right: 1px;
  bottom: 1px;
  width: 2rem;
  border-left: 1px solid #c6daf6;
  border-radius: 0 9px 9px 0;
  background: linear-gradient(180deg, #ecf4ff 0%, #deebfe 100%);
  pointer-events: none;
}

.dashboard-filter-card--wp .dashboard-select-wrap::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0.67rem;
  margin-top: -2px;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 7px solid #274f82;
  pointer-events: none;
}

.dashboard-filter-card--wp .dashboard-select-wrap--disabled::before {
  border-left-color: #d7e3f6;
  background: linear-gradient(180deg, #edf2fb 0%, #e2e9f5 100%);
}

.dashboard-filter-card--wp .dashboard-select-wrap--disabled::after {
  border-top-color: #95a8c6;
}

.dashboard-filter-card--wp .form-select {
  min-height: 32px;
  border-color: #c9dcf7;
  border-radius: 10px;
  padding-top: 0.2rem;
  padding-bottom: 0.2rem;
  padding-right: 2.35rem;
  font-weight: 600;
  color: #28466d;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-color: #ffffff;
  background-image: linear-gradient(180deg, #ffffff 0%, #f6faff 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.95), 0 1px 2px rgba(29, 76, 139, 0.08);
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.dashboard-filter-card--wp .form-select:hover:not(:disabled) {
  border-color: #a9c8f0;
  background-color: #fcfeff;
  background-image: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.dashboard-filter-card--wp .form-select:focus {
  border-color: #6fa5ec;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    0 0 0 0.2rem rgba(47, 126, 212, 0.18);
}

.dashboard-filter-card--wp .form-select:disabled {
  color: #7d92b4;
  border-color: #d8e4f7;
  background-image: linear-gradient(180deg, #f5f8fd 0%, #eef3fb 100%);
  cursor: default;
  box-shadow: none;
}

.dashboard-range-preset--stats {
  padding: 4px;
  border: 1px solid #d3e2fb;
  border-radius: 12px;
  background: #edf4ff;
  gap: 4px;
}

.dashboard-range-preset--stats .dashboard-chart-toggle {
  flex: 1 1 calc(25% - 4px);
  min-width: 84px;
  border-color: transparent;
  background: #f4f8ff;
  color: #35557f;
}

.dashboard-range-preset--stats .dashboard-chart-toggle--active {
  border-color: #2f7ed4;
  box-shadow: 0 1px 2px rgba(26, 58, 106, 0.16);
}

.dashboard-wp-custom-range {
  margin-top: 8px;
  padding: 10px;
  border: 1px dashed #c9dcfb;
  border-radius: 12px;
  background: #f8fbff;
}

.dashboard-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.dashboard-chip {
  border: 1px solid #dae0ef;
  background: #ffffff;
  color: #324462;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
}

.dashboard-empty-state {
  border: 1px dashed #d6e3f8;
  border-radius: 12px;
  padding: 22px;
  color: #5f6f89;
  background: #fcfdff;
}

.dashboard-empty-state--compact {
  padding: 14px;
}

.dashboard-empty-state__title {
  color: #24456f;
  font-size: 15px;
  font-weight: 700;
}

.dashboard-empty-state__text {
  margin-top: 2px;
  font-size: 13px;
}

.dashboard-stat {
  border-radius: 12px;
  border: 1px solid #dce7fa;
  background: #fff;
  padding: 11px;
  min-height: 120px;
  box-shadow: 0 8px 20px rgba(24, 55, 100, 0.08);
}

.dashboard-stat__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.dashboard-stat__label {
  color: #4f688d;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.dashboard-stat__icon {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
}

.dashboard-stat__value-wrap {
  margin-top: 6px;
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.dashboard-stat__value {
  margin-top: 0;
  color: #1f3a63;
  font-size: 30px;
  line-height: 1.12;
  font-weight: 800;
}

.dashboard-stat__change {
  font-size: 14px;
  line-height: 1;
  font-weight: 700;
  color: #607089;
}

.dashboard-stat__change--up {
  color: #1f8b4c;
}

.dashboard-stat__change--down {
  color: #c74040;
}

.dashboard-stat__change--flat {
  color: #607089;
}

.dashboard-stat__meta {
  margin-top: 4px;
  color: #617a9f;
  font-size: 12px;
  line-height: 1.3;
}

.dashboard-stat--compact {
  min-height: 88px;
  padding: 8px 10px;
}

.dashboard-stat--compact .dashboard-stat__label {
  font-size: 10px;
}

.dashboard-stat--compact .dashboard-stat__icon {
  width: 24px;
  height: 24px;
  font-size: 13px;
}

.dashboard-stat--compact .dashboard-stat__value-wrap {
  margin-top: 4px;
  gap: 6px;
}

.dashboard-stat--compact .dashboard-stat__value {
  font-size: 22px;
}

.dashboard-stat--compact .dashboard-stat__change {
  font-size: 12px;
}

.dashboard-stat--tokens .dashboard-stat__icon {
  background: rgba(43, 103, 214, 0.14);
  color: #2758b2;
}

.dashboard-stat--ai-req .dashboard-stat__icon {
  background: rgba(31, 161, 172, 0.14);
  color: #1a7886;
}

.dashboard-stat--img-req .dashboard-stat__icon {
  background: rgba(185, 86, 160, 0.14);
  color: #8a3f79;
}

.dashboard-stat--published .dashboard-stat__icon {
  background: rgba(43, 165, 111, 0.14);
  color: #1f7d55;
}

.dashboard-stat--content-req .dashboard-stat__icon {
  background: rgba(140, 92, 200, 0.14);
  color: #6843a5;
}

.dashboard-stat--streams .dashboard-stat__icon {
  background: rgba(209, 122, 47, 0.15);
  color: #9e591f;
}

.dashboard-stat--wp-visits .dashboard-stat__icon {
  background: rgba(37, 124, 196, 0.16);
  color: #1d5f98;
}

.dashboard-stat--wp-unique .dashboard-stat__icon {
  background: rgba(39, 163, 120, 0.16);
  color: #1f7d5b;
}

.dashboard-stat--wp-actions .dashboard-stat__icon {
  background: rgba(224, 145, 44, 0.2);
  color: #a5671e;
}

.dashboard-stat--wp-time .dashboard-stat__icon {
  background: rgba(120, 104, 198, 0.18);
  color: #5a4ba8;
}

.dashboard-stat--wp-bounce .dashboard-stat__icon {
  background: rgba(188, 90, 90, 0.17);
  color: #9b4343;
}

.dashboard-stat--wp-streams .dashboard-stat__icon {
  background: rgba(66, 133, 244, 0.18);
  color: #2d62b4;
}

.dashboard-panel {
  background: #fff;
  border: 1px solid #dce7fa;
  border-radius: 14px;
  overflow: hidden;
}

.dashboard-panel__header {
  padding: 10px 12px;
  border-bottom: 1px solid #e8eef8;
  background: #f8fbff;
  font-weight: 700;
  color: #24456f;
}

.dashboard-panel__header--split {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.dashboard-chart-controls {
  display: inline-flex;
  gap: 6px;
  flex-wrap: wrap;
}

.dashboard-chart-toggle {
  border: 1px solid #cfe0fb;
  background: #f6f9ff;
  color: #35557f;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  padding: 5px 10px;
}

.dashboard-chart-toggle--active {
  background: #2f7ed4;
  border-color: #2f7ed4;
  color: #fff;
}

.dashboard-chart-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.dashboard-line-chart {
  border: 1px solid #e1eaf9;
  border-radius: 12px;
  background: linear-gradient(180deg, #fbfdff 0%, #f7faff 100%);
  padding: 8px;
}

.dashboard-line-chart svg {
  width: 100%;
  height: 250px;
  display: block;
}

.dashboard-line-chart__tick-label {
  fill: #6a7f9f;
  font-size: 11px;
  font-weight: 600;
  text-anchor: start;
}

.dashboard-line-chart__axis {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  color: #5f7498;
  font-size: 12px;
  font-weight: 600;
}

.dashboard-table thead th {
  color: #607089;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 10px 14px;
  border-bottom-width: 1px;
  border-color: #e9edf6;
  background: #fbfcfe;
}

.dashboard-table tbody td {
  border-color: #edf1f7;
  padding: 10px 14px;
  color: #2b3f61;
  font-size: 13px;
}

.dashboard-mini-bar {
  height: 8px;
  border-radius: 999px;
  background: #edf3ff;
  overflow: hidden;
  min-width: 68px;
}

.dashboard-mini-bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #4a8fde 0%, #2a64b1 100%);
}

.dashboard-sync-title {
  font-size: 12px;
  font-weight: 700;
  color: #3c5478;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
}

.dashboard-sync-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
}

.dashboard-sync-item {
  border: 1px solid #dde8fa;
  border-radius: 10px;
  padding: 8px;
  background: #fbfdff;
}

.dashboard-sync-item__label {
  display: block;
  color: #617a9f;
  font-size: 11px;
  font-weight: 600;
}

.dashboard-sync-item__value {
  display: block;
  color: #1f3a63;
  font-size: 13px;
  font-weight: 700;
}

.loading-wrap {
  margin-top: 4px;
}

.skeleton-row {
  height: 68px;
  background: linear-gradient(90deg, #f2f4f8 25%, #eef1f6 37%, #f2f4f8 63%);
  background-size: 400% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
  border-radius: 10px;
  margin-bottom: 10px;
}

@keyframes shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

@media (max-width: 1200px) {
  .dashboard-sync-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 992px) {
  .dashboard-hero {
    margin: 0 12px 12px;
    padding: 12px;
    flex-direction: column;
    align-items: flex-start;
  }

  .dashboard-hero__refresh {
    width: 100%;
  }

  .dashboard-tabs {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .dashboard-tab {
    width: 100%;
  }

  .dashboard-stat {
    min-height: 112px;
  }

  .dashboard-stat--compact {
    min-height: 88px;
  }

  .dashboard-panel__header--split {
    flex-direction: column;
    align-items: flex-start;
  }

  .dashboard-sync-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .dashboard-filter-actions--stats {
    justify-content: flex-start;
  }

  .dashboard-range-preset--stats {
    width: 100%;
  }

  .dashboard-range-preset--stats .dashboard-chart-toggle {
    flex: 1 1 calc(50% - 4px);
    min-width: 0;
  }

  .dashboard-filter-actions .btn {
    flex: 1 1 calc(50% - 6px);
  }
}

@media (max-width: 576px) {
  .dashboard-sync-grid {
    grid-template-columns: 1fr;
  }

  .dashboard-filter-actions .btn {
    flex: 1 1 100%;
  }
}
</style>
